import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { errMsg, useApi } from "@/lib/api";

const NONE = "";

export function useOptions(fields) {
  const api = useApi();
  const [opts, setOpts] = useState({});
  const keys = [...new Set(fields.map((f) => (f.type.startsWith("ref:") ? f.type.slice(4) : ["course", "courses"].includes(f.type) ? "courses" : null)).filter(Boolean))].join(",");
  useEffect(() => {
    keys.split(",").filter(Boolean).forEach((k) => api.get(`/admin/options/${k}`).then((r) => setOpts((o) => ({ ...o, [k]: r.data }))).catch(() => {}));
  }, [api, keys]);
  return opts;
}

export const optKey = (f) => (f.type.startsWith("ref:") ? f.type.slice(4) : "courses");

const toLocal = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function Field({ f, value, onChange, opts }) {
  const id = `f-${f.name}`;
  const tid = `field-${f.name}`;
  if (f.type === "bool") {
    return <label className="flex items-center gap-2 text-sm text-slate-800"><Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} data-testid={tid} /> {f.label}</label>;
  }
  let input;
  if (f.type === "textarea" || f.type === "lines") {
    input = <Textarea id={id} rows={f.type === "lines" ? 4 : 6} value={Array.isArray(value) ? value.join("\n") : value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  } else if (f.type === "select" || f.type.startsWith("ref:") || f.type === "course") {
    const list = f.type === "select" ? f.options.map((o) => ({ id: o, label: o || "—" })) : [{ id: NONE, label: "—" }, ...(opts[optKey(f)] || [])];
    input = (
      <select id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid={tid}>
        {list.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    );
  } else if (f.type === "courses") {
    const sel = value || [];
    input = (
      <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-300 p-2" data-testid={tid}>
        {(opts.courses || []).map((o) => (
          <label key={o.id} className="flex items-center gap-2 text-sm"><Checkbox checked={sel.includes(o.id)} onCheckedChange={(v) => onChange(v ? [...sel, o.id] : sel.filter((s) => s !== o.id))} /> {o.label}</label>
        ))}
        {!(opts.courses || []).length && <p className="text-sm text-slate-500">No courses yet</p>}
      </div>
    );
  } else {
    const type = { number: "number", date: "date", datetime: "datetime-local", email: "email" }[f.type] || "text";
    const v = f.type === "datetime" ? toLocal(value) : value ?? "";
    input = <Input id={id} type={type} value={v} onChange={(e) => onChange(f.type === "datetime" && e.target.value ? new Date(e.target.value).toISOString() : e.target.value)} data-testid={tid} />;
  }
  return <div className="space-y-1.5"><Label htmlFor={id}>{f.label}{f.required && " *"}</Label>{input}</div>;
}

export function ResourceForm({ res, record, open, onClose, onSaved }) {
  const api = useApi();
  const opts = useOptions(res.fields);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(record ? { ...record } : Object.fromEntries(res.fields.filter((x) => "default" in x).map((x) => [x.name, x.default])));
  }, [open, record, res]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = Object.fromEntries(res.fields.map((x) => [x.name, x.type === "lines" && typeof f[x.name] === "string" ? f[x.name].split("\n") : f[x.name]]).filter(([, v]) => v !== undefined));
    try {
      if (record) await api.patch(`/admin/r/${res.key}/${record.id}`, body);
      else await api.post(`/admin/r/${res.key}`, body);
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="resource-form">
        <DialogHeader><DialogTitle>{record ? "Edit" : "Add"} · {res.label}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {res.fields.map((x) => <Field key={x.name} f={x} value={f[x.name]} opts={opts} onChange={(v) => setF((o) => ({ ...o, [x.name]: v }))} />)}
          <DialogFooter><Button type="submit" disabled={busy} className="bg-[#222d99] hover:bg-[#1a237e]" data-testid="resource-form-save">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
